// SPDX-License-Identifier: Elastic-2.0
/**
 * Slider — themed wrapper around <input type="range">.
 *
 * Why a native range input: the platform gives us correct keyboard
 * semantics for free (Arrow keys, Home/End, PageUp/PageDown step,
 * `aria-valuemin/max/now`). Restyling track + thumb in CSS Modules is
 * cheaper than reimplementing all of that with divs.
 *
 * The component clamps the controlled `value` into `[min, max]` before
 * rendering so a stale store value never produces an invalid native
 * state. `onChange` always fires with the clamped result.
 */
import type { CSSProperties } from "react";
import { useId } from "react";

import styles from "./Slider.module.css";

export interface SliderProps {
  /** Current value (controlled). */
  value: number;
  /** Called when the user moves the slider. Always receives a clamped value. */
  onChange: (value: number) => void;
  /** Minimum bound. Default 0. */
  min?: number;
  /** Maximum bound. Default 100. */
  max?: number;
  /** Step size. Default 1. */
  step?: number;
  /** Visible label rendered above the slider. */
  label?: string;
  /**
   * Optional formatter for the displayed value (e.g. `(v) => `${v}°`).
   * Falls back to `String(value)` when omitted.
   */
  formatValue?: (value: number) => string;
  /** Disable interaction. */
  disabled?: boolean;
  /** ARIA label override when `label` isn't suitable for SR users. */
  "aria-label"?: string;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  formatValue,
  disabled,
  "aria-label": ariaLabelOverride,
}: SliderProps): JSX.Element {
  const id = useId();
  const safeValue = clamp(value, min, max);
  const range = max - min;
  const fillPct = range > 0 ? ((safeValue - min) / range) * 100 : 0;

  const display = formatValue ? formatValue(safeValue) : String(safeValue);

  const wrapStyle = { "--fill-pct": fillPct } as CSSProperties & Record<string, string | number>;

  return (
    <div className={styles.wrap}>
      {(label || formatValue) && (
        <div className={styles.header}>
          {label ? (
            <label htmlFor={id} className={styles.label}>
              {label}
            </label>
          ) : (
            <span />
          )}
          <span className={styles.value} aria-live="polite">
            {display}
          </span>
        </div>
      )}
      <input
        id={id}
        type="range"
        className={styles.range}
        min={min}
        max={max}
        step={step}
        value={safeValue}
        disabled={disabled}
        aria-label={ariaLabelOverride ?? (label ? undefined : "Slider")}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={safeValue}
        aria-valuetext={display}
        style={wrapStyle}
        onChange={(event) => {
          const raw = Number(event.currentTarget.value);
          onChange(clamp(raw, min, max));
        }}
      />
    </div>
  );
}
